import { useEffect, useRef } from "react";
import { WebchatApi } from "@/lib/api";
import { resolveSettings, readVisitorId, writeVisitorId } from "@/lib/env";
import { awaitIdentity } from "@/lib/host-bridge";
import { applyWidgetTheme, useSession } from "@/store/session-store";

/**
 * Bootstraps the visitor session on mount. Runs exactly once per widget
 * lifecycle — REST `/session` sets up the Contact, Conversation, and
 * issues the short-lived visitor JWT. The JWT is kept in memory only
 * (not persisted) so a closed tab re-runs the bootstrap and can
 * revalidate allowed-origin.
 *
 * Returns the WebchatApi instance (memoized) so other hooks / components
 * can call authenticated endpoints.
 */
export function useSession_(): {
  api: WebchatApi | null;
} {
  const apiRef = useRef<WebchatApi | null>(null);
  const setBootstrap = useSession((s) => s.setBootstrap);
  const setError = useSession((s) => s.setError);
  const setHostTheme = useSession((s) => s.setHostTheme);
  const status = useSession((s) => s.status);

  useEffect(() => {
    if (status !== "idle") return;
    useSession.setState({ status: "loading" });

    const settings = resolveSettings();
    if (!settings) {
      setError("Missing siteKey — widget install is incomplete");
      return;
    }

    void (async () => {
      try {
        // Give the host iframe loader a brief window to push identity via
        // postMessage before we fire /session. If nothing arrives we
        // proceed anonymously. The 350ms budget keeps TTI low.
        const hostIdentity = await awaitIdentity(350);
        const enriched = {
          ...settings,
          identity: hostIdentity.identity ?? settings.identity,
          identityHash: hostIdentity.identityHash ?? settings.identityHash,
        };
        const api = new WebchatApi(enriched);
        apiRef.current = api;

        const visitorId = readVisitorId();
        const boot = await api.createSession(visitorId);
        writeVisitorId(boot.visitorId);
        api.setSessionToken(boot.sessionToken);

        const hostTheme = hostIdentity.hostTheme ?? null;
        if (hostTheme) setHostTheme(hostTheme);

        setBootstrap({
          siteKey: enriched.siteKey,
          apiUrl: enriched.apiUrl,
          visitorId: boot.visitorId,
          conversationId: boot.conversationId,
          sessionToken: boot.sessionToken,
          branding: boot.branding,
          history: boot.history,
        });

        applyWidgetTheme(boot.branding, hostTheme);

        if (boot.branding.autoOpen) {
          useSession.getState().setOpen(true);
        }
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to start session";
        setError(msg);
      }
    })();
  }, [status, setBootstrap, setError, setHostTheme]);

  return { api: apiRef.current };
}
